import { useEffect, useRef } from 'react';
import {
  type Play,
  type PlayType,
  type PlayPresentation,
} from '@bbkAdminRedux/rtkq/play.slice';
import { PlayObjectProperty } from '@bbkAdminComponents/play/play-context';

interface DuplicatePlayConfig {
  playToDuplicate: Play | undefined;
  duplicateFromPlayId: string | undefined;
  playName?: string;
  playType?: PlayType;
  presentation?: PlayPresentation;
  brandId?: string;
  setPlayObjectProperty: <T extends PlayObjectProperty>(
    field: T,
    value: any
  ) => void;
  onDuplicationComplete?: () => void;
}

export const useDuplicatePlay = ({
  playToDuplicate,
  duplicateFromPlayId,
  playName,
  playType,
  presentation,
  brandId,
  setPlayObjectProperty,
  onDuplicationComplete,
}: DuplicatePlayConfig) => {
  const hasDuplicated = useRef(false);

  useEffect(() => {
    if (hasDuplicated.current) {
      return;
    }

    if (!playToDuplicate || !duplicateFromPlayId) {
      return;
    }

    if (playName) {
      setPlayObjectProperty(PlayObjectProperty.PLAY_TITLE, playName);
    }

    if (playType) {
      setPlayObjectProperty(PlayObjectProperty.PLAY_TYPE, playType);
    }

    if (presentation) {
      setPlayObjectProperty(PlayObjectProperty.PRESENTATION, presentation);
    }

    if (brandId) {
      setPlayObjectProperty(PlayObjectProperty.BRAND_ID, brandId);
    }

    if (playToDuplicate.rule_id) {
      setPlayObjectProperty(
        PlayObjectProperty.AUDIENCE_ID,
        playToDuplicate.rule_id
      );
    }

    if (playToDuplicate.trigger_rules) {
      setPlayObjectProperty(
        PlayObjectProperty.PLAY_TRIGGER,
        playToDuplicate.trigger_rules
      );
    }

    const targetingToCopy =
      playToDuplicate.draft_targeting || playToDuplicate.targeting;

    if (targetingToCopy?.routing_percents) {
      if (targetingToCopy.routing_percents[0]) {
        setPlayObjectProperty(
          PlayObjectProperty.CONTROL_NODE,
          targetingToCopy.routing_percents[0]
        );
      }

      if (targetingToCopy.routing_percents[1]) {
        setPlayObjectProperty(
          PlayObjectProperty.OFFER_NODE,
          targetingToCopy.routing_percents[1]
        );
      }
    }

    hasDuplicated.current = true;

    if (onDuplicationComplete) {
      onDuplicationComplete();
    }
  }, [
    playToDuplicate,
    duplicateFromPlayId,
    playName,
    playType,
    presentation,
    brandId,
    setPlayObjectProperty,
    onDuplicationComplete,
  ]);
};
